import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Image as ImageIcon, X, Loader2, Send } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";

export default function CreatePost() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.url);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!content.trim() && !image) return;
    setPosting(true);
    try {
      await api.post("/posts", { content, image_url: image });
      setContent("");
      setImage(null);
      qc.invalidateQueries({ queryKey: ["feed"] });
      toast.success("Posted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
      <div className="flex gap-3">
        <Avatar user={user} size={44} />
        <textarea
          data-testid="create-post-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`What's on your mind, ${user?.name?.split(" ")[0]}?`}
          rows={2}
          className="flex-1 bg-input rounded-2xl px-4 py-3 text-sm resize-none outline-none border border-transparent focus:border-[#6C0BA9]/60 transition-colors"
        />
      </div>

      {image && (
        <div className="relative mt-3 ml-14 rounded-xl overflow-hidden border border-border">
          <img src={mediaUrl(image)} alt="preview" className="w-full max-h-80 object-cover" />
          <button data-testid="remove-image-btn" onClick={() => setImage(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border ml-0">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="post-image-file" />
        <button data-testid="add-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-accent transition-colors text-sm font-medium text-[#C084FC]">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />} Photo
        </button>
        <Button data-testid="submit-post-btn" onClick={submit} disabled={posting || (!content.trim() && !image)} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white font-semibold px-5 gap-2">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
        </Button>
      </div>
    </div>
  );
}
